import { apiClient } from './apiClient';

/**
 * Language-neutral sentinel stamped on `performedBy` when an event is produced by an automated
 * process (cron job, sync pipeline) rather than a user. Mirrors the backend `SystemActors.System`
 * constant; the UI maps it to a localized label in the viewer's language. Keep both in sync.
 */
export const SYSTEM_ACTOR = '__SYSTEM__';

/**
 * Language-neutral sentinels stored in a RELANCE_REACTIVATED event's `reason` when the
 * reactivation is automatic (cron snooze-expiry or sync re-surface). Mirror the backend
 * `ReactivationReasons` constants; the UI maps each to a localized label in the viewer's
 * language. A manual reactivation carries the user's free-text reason instead. Keep in sync.
 */
export const AUTO_REASON_SNOOZE_EXPIRED = '__AUTO_SNOOZE_EXPIRED__';
export const AUTO_REASON_DEBT_INCREASED = '__AUTO_DEBT_INCREASED__';
export const AUTO_REASON_DEBT_CHANGED = '__AUTO_DEBT_CHANGED__';

interface BaseHistoryEventDto {
    id: string;
    customerId: string | null;
    customerName: string | null;
    occurredAt: string;
    performedBy: string | null;
}

export interface RelanceCompletedEventDto extends BaseHistoryEventDto {
    type: 'RELANCE_COMPLETED';
    invoiceNumbers: string[];
    amountDue: number | null;
    recipientEmail: string | null;
    subject: string | null;
    attachmentCount: number;
    ccEmail?: string | null;
    bccEmail?: string | null;
    replyToEmail?: string | null;
}

export interface NoteAddedEventDto extends BaseHistoryEventDto {
    type: 'NOTE_ADDED';
    noteContent: string | null;
    amountDue: number | null;
    recipientEmail: string | null;
}

export interface RelanceDeactivatedEventDto extends BaseHistoryEventDto {
    type: 'RELANCE_DEACTIVATED';
    amountDue: number | null;
    snoozeUntil: string | null;
    note: string | null;
    relanceId: string | null;
    // Current state of the referenced relance: false = still deactivated (reactivatable),
    // true = already reactivated, null = relance no longer exists. The Réactiver action is
    // only offered when this is false.
    relanceIsActive: boolean | null;
}

export interface RelanceReactivatedEventDto extends BaseHistoryEventDto {
    type: 'RELANCE_REACTIVATED';
    amountDue: number | null;
    reason: string | null;
}

export interface RelancePaidEventDto extends BaseHistoryEventDto {
    type: 'RELANCE_PAID';
    amountReceived: number | null;
    remainingBalance: number | null;
    isFullPayment: boolean | null;
    note: string | null;
    relanceId: string | null;
}

// Aggregate entry recorded when a batch of Transphere payments is applied in AcombaGO's Encaissement
// window. Not tied to a customer (customerId is always null): it summarizes the whole batch.
export interface PaymentsAppliedEventDto extends BaseHistoryEventDto {
    type: 'TRANSPHERE_PAYMENTS_APPLIED';
    customersUpdated: number;
    invoicesApplied: number;
}

export type HistoryEventItemDto =
    | RelanceCompletedEventDto
    | NoteAddedEventDto
    | RelanceDeactivatedEventDto
    | RelanceReactivatedEventDto
    | RelancePaidEventDto
    | PaymentsAppliedEventDto;

export interface HistoryPageDto {
    items: HistoryEventItemDto[];
    totalCount: number;
}

export interface GetHistoryParams {
    customerId?: string;
    page: number;
    pageSize: number;
    // Restrict to a single event type (e.g. 'RELANCE_COMPLETED' for the client follow-up timeline).
    // Omitted → all event types are returned.
    eventType?: HistoryEventItemDto['type'];
    // Sort on occurredAt. Defaults to newest-first ('desc'), which is what the follow-up timeline
    // reads; it recovers the chronological rank as `events.length - index`. 'asc' reads oldest → newest.
    orderDirection?: 'asc' | 'desc';
}

export const eventHistoryService = {
    getHistory: (params: GetHistoryParams) => {
        const qs = new URLSearchParams({
            page: String(params.page),
            pageSize: String(params.pageSize),
            orderDirection: params.orderDirection ?? 'desc',
        });
        if (params.customerId) qs.set('customerId', params.customerId);
        if (params.eventType) qs.set('eventType', params.eventType);
        return apiClient.get<HistoryPageDto>(`/events?${qs.toString()}`);
    },
};
